/**
 * Which build of the UI this page runs, and whether a newer one is served. Deploys come only
 * from master, so a served version that differs from this build's is a newer one.
 */

import { z } from 'zod';

import { MINUTE_MS } from '@/lib/timeUnits';
import type { Unsubscribe } from '@/types/common';
import type { VersionWatch } from '@/types/contract';

import { fetchServedFile } from './servedFile';

/**
 * The commit this bundle was built from. Empty for `npm run dev` and the tests, which are never
 * outdated.
 */
export const BUILD_VERSION: string = __UI_VERSION__;

// One read answers every check of a burst: a list dropping 300 rows asks once.
const FRESH_FOR_MS = MINUTE_MS;
const VERSION_FILE_URL = '/version.json';

const VersionFileSchema = z.object({ version: z.string() });

interface VersionWatchDeps {
    build: string;
    /** The served version, or null when there is none to compare with. */
    fetchServed: () => Promise<string | null>;
    now: () => number;
}

/** Compares this build's stamp with the served one, at most once a minute. */
export class BuildVersionWatch implements VersionWatch {
    private readonly build: string;

    private readonly fetchServed: () => Promise<string | null>;

    private readonly now: () => number;

    private isOutdated = false;

    private checkedAtMs = Number.NEGATIVE_INFINITY;

    private pending: Promise<boolean> | null = null;

    private readonly listeners = new Set<() => void>();

    constructor({ build, fetchServed, now }: VersionWatchDeps) {
        this.build = build;
        this.fetchServed = fetchServed;
        this.now = now;
    }

    check(): Promise<boolean> {
        if (this.build === '') {
            return Promise.resolve(false);
        }
        if (this.pending !== null) {
            return this.pending;
        }
        if (this.now() - this.checkedAtMs < FRESH_FOR_MS) {
            return Promise.resolve(this.isOutdated);
        }
        return this.read();
    }

    getSnapshot(): boolean {
        return this.isOutdated;
    }

    subscribe(listener: () => void): Unsubscribe {
        this.listeners.add(listener);
        return (): void => {
            this.listeners.delete(listener);
        };
    }

    private read(): Promise<boolean> {
        this.pending = this.fetchServed()
            .catch((): null => null)
            .then((servedText): boolean => {
                this.checkedAtMs = this.now();
                this.pending = null;
                return this.settle(servedText);
            });
        return this.pending;
    }

    private settle(servedText: string | null): boolean {
        const isOutdatedNow = servedText !== null && servedText !== this.build;
        if (isOutdatedNow !== this.isOutdated) {
            this.isOutdated = isOutdatedNow;
            for (const listener of [...this.listeners]) {
                listener();
            }
        }
        return this.isOutdated;
    }
}

/** What the server serves as current; null where it serves no version file. */
export async function fetchServedVersion(): Promise<string | null> {
    const file = await fetchServedFile(VERSION_FILE_URL, VersionFileSchema);
    return file === null ? null : file.version;
}

export const versionWatch: VersionWatch = new BuildVersionWatch({
    build: BUILD_VERSION,
    fetchServed: fetchServedVersion,
    now: (): number => Date.now(),
});
